// validate-git-url-scheme-allowlist.ts
//
// VULN-4a: Shared scheme-allowlist gate cho git URL trước khi truyền vào
// `git ls-remote`/`git clone` (spawnSync/simple-git). Git tự hỗ trợ nhiều
// transport ("smart" schemes) — `ext::` cho phép chạy lệnh SHELL TÙY Ý
// (vd "ext::sh -c 'rm -rf ~'"), `file://` cho phép đọc path local ngoài ý
// định. Cả 2 hàm gọi git (verify-git-remote-accessible.ts, clone-code-repo-
// into-src.ts) trước đây KHÔNG check scheme → gộp về đây theo pattern
// validate-endpoint-host-allowlist.ts (VULN-2) để tránh 2 nơi lệch nhau khi
// cần siết/nới allowlist.
//
// Chỉ chấp nhận: https://, ssh://, và scp-style SSH ("git@host:path" — KHÔNG
// có scheme prefix chuẩn URL, là shorthand riêng của git/ssh). Từ chối mọi
// scheme khác (ext::, file://, git://, ...).
//
// M1 defense-in-depth (code review): cả 2 caller hiện tại spawn git qua
// argv-array (KHÔNG shell:true) nên option-smuggling/shell-metachar KHÔNG
// khai thác được ngay bây giờ — nhưng siết thêm ở đây để không phụ thuộc mãi
// vào "caller luôn spawn đúng cách". Reject thêm 2 nhóm:
//   1. Host bắt đầu bằng "-" (SSH option smuggling kiểu "-oProxyCommand=...").
//   2. Shell metachar nguy hiểm xuất hiện bất kỳ đâu trong URL: ; | & ` $( < >
//      và newline. Đây không phải path hợp lệ trong git URL nên reject an toàn.
// Fail-safe: URL không chắc chắn hợp lệ → reject, không cố gắng "sửa" cho pass.
// scp-style: "user@host:path" — không có "://" nên check riêng bằng regex đơn
// giản (username hợp lệ + "@" + host + ":" + path), không dùng URL parser vì
// đây không phải URL chuẩn.
const SCP_STYLE_PATTERN = /^([\w.-]+)@([\w.-]+):(.*)$/;

// Shell metachar / control-char nguy hiểm — không hợp lệ trong bất kỳ phần nào
// (host/user/path) của 1 git URL thật. Reject để fail-safe.
const DANGEROUS_METACHAR_PATTERN = /[;|&`$<>\n\r]|\$\(/;

// Host không được bắt đầu bằng "-" — chống option smuggling khi host được
// truyền cho ssh/git dạng argument (vd "-oProxyCommand=...").
function isSuspiciousHost(host: string): boolean {
  return host.length === 0 || host.startsWith("-");
}

export function assertAllowedGitUrlScheme(url: string): void {
  if (DANGEROUS_METACHAR_PATTERN.test(url)) {
    throw new Error(
      `Git URL chứa ký tự không hợp lệ (metachar/control-char): "${url}". Chỉ chấp nhận https://, ssh://, hoặc git@host:path (SSH scp-style).`,
    );
  }

  if (url.startsWith("ssh://")) {
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      throw new Error(
        `Git URL scheme không được phép: "${url}". Chỉ chấp nhận https://, ssh://, hoặc git@host:path (SSH scp-style).`,
      );
    }
    if (isSuspiciousHost(parsed.hostname) || isSuspiciousHost(parsed.username)) {
      throw new Error(
        `Git URL ssh:// có host/user không hợp lệ (bắt đầu bằng "-" hoặc rỗng): "${url}".`,
      );
    }
    return;
  }

  if (url.startsWith("https://")) {
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      throw new Error(
        `Git URL scheme không được phép: "${url}". Chỉ chấp nhận https://, ssh://, hoặc git@host:path (SSH scp-style).`,
      );
    }
    if (isSuspiciousHost(parsed.hostname)) {
      throw new Error(
        `Git URL https:// có host không hợp lệ (bắt đầu bằng "-" hoặc rỗng): "${url}".`,
      );
    }
    return;
  }

  const scpMatch = SCP_STYLE_PATTERN.exec(url);
  if (scpMatch) {
    const user = scpMatch[1] ?? "";
    const host = scpMatch[2] ?? "";
    if (isSuspiciousHost(host) || isSuspiciousHost(user)) {
      throw new Error(
        `Git URL scp-style có host/user không hợp lệ (bắt đầu bằng "-" hoặc rỗng): "${url}".`,
      );
    }
    return;
  }

  throw new Error(
    `Git URL scheme không được phép: "${url}". Chỉ chấp nhận https://, ssh://, hoặc git@host:path (SSH scp-style).`,
  );
}
